import { useEffect, useMemo, useState } from "react";
import client from "@/lib/glossyApi";
import { PenLine, Image as ImageIcon, Film, Receipt, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Header } from "./shared";

const RANGES = [
  { id: "day", label: "Today", days: 1 },
  { id: "week", label: "This week", days: 7 },
  { id: "month", label: "This month", days: 30 },
  { id: "all", label: "All time", days: 0 },
];

const fmtGbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const rowCost = (a) => (a.cost_gbp != null ? Number(a.cost_gbp) : (a.kind === "image" ? 0.04 : a.kind === "video" ? 0.5 : 0.01));

function FeaturesPanel() {
  const [items, setItems] = useState(null);
  const load = () => client.get("/admin/features").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const patch = async (key, body, msg) => {
    try {
      await client.patch(`/admin/features/${key}`, body);
      toast.success(msg);
      load();
    } catch { toast.error("Couldn't update — try again."); }
  };

  if (!items) return null;
  return (
    <div className="border border-ink/15 bg-white" data-testid="features-panel">
      <div className="p-5 sm:p-6 border-b border-ink/10">
        <p className="overline text-ink/60">Feature meters — last 30 days</p>
        <p className="font-sans text-sm text-ink/70 mt-1.5">Each function's spend on its own dial. Switch the reader-facing ones off any time — they vanish from the site instantly, and can't spend a penny while off.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3">
        {items.map((f) => (
          <div key={f.key} className="p-5 border-t border-ink/10 sm:border-r" data-testid={`feature-${f.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-serif text-lg leading-tight">{f.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-ink/40 mt-1">{f.note}</p>
              </div>
              {f.togglable && (
                <button
                  onClick={() => patch(f.key, { enabled: !f.enabled }, f.enabled ? `${f.name} switched off — hidden from the site.` : `${f.name} back on.`)}
                  className={`shrink-0 w-11 h-6 rounded-full relative transition-colors ${f.enabled ? "bg-ink" : "bg-ink/20"}`}
                  data-testid={`feature-toggle-${f.key}`}
                  aria-label={`Toggle ${f.name}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-cream transition-all ${f.enabled ? "left-[22px]" : "left-0.5"}`} />
                </button>
              )}
            </div>
            <p className="font-serif text-3xl mt-3">{fmtGbp(f.month_gbp)}</p>
            {f.cappable && (
              <div className="flex items-center gap-1.5 mt-3">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink/40">Cap £</span>
                <input
                  type="number" min="0" step="1" placeholder="none"
                  defaultValue={f.cap_gbp ?? ""}
                  onBlur={(e) => { const v = e.target.value; if (String(f.cap_gbp ?? "") !== v) patch(f.key, { cap_gbp: v || null }, v ? `${f.name} capped at £${v}/month.` : `${f.name} cap removed.`); }}
                  className="w-20 bg-white border border-ink/25 px-2 py-1 font-sans text-sm focus:outline-none focus:border-ink"
                  data-testid={`feature-cap-${f.key}`}
                />
                {f.cap_gbp && f.month_gbp >= f.cap_gbp && <span className="font-mono text-[10px] uppercase text-red-600">At cap — paused</span>}
              </div>
            )}
            {!f.enabled && <p className="font-mono text-[10px] uppercase tracking-wider text-red-600 mt-2">Off — hidden from site</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Credits() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState({ range: "week", kind: "all", member_id: "all" });
  const [loading, setLoading] = useState(true);
  const [budgetInput, setBudgetInput] = useState("");

  const loadSummary = () => client.get("/admin/credits-summary").then((r) => {
    setSummary(r.data);
    setBudgetInput(r.data?.monthly_budget_gbp != null ? String(r.data.monthly_budget_gbp) : "");
  }).catch(() => setSummary(null));
  const loadMembers = () => client.get("/admin/members").then((r) => setMembers(r.data)).catch(() => setMembers([]));

  const loadActivity = async () => {
    setLoading(true);
    const params = { limit: 500 };
    if (filter.kind !== "all") params.kind = filter.kind;
    if (filter.member_id !== "all") params.member_id = filter.member_id;
    if (filter.range !== "all") {
      const d = RANGES.find((r) => r.id === filter.range)?.days || 7;
      params.since = new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString();
    }
    try {
      const r = await client.get("/admin/ai-activity", { params });
      setItems(r.data || []);
    } catch { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { loadSummary(); loadMembers(); }, []);
  useEffect(() => { loadActivity(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCost = useMemo(() => items.reduce((s, a) => s + rowCost(a), 0), [items]);
  const filteredImages = useMemo(() => items.filter((a) => a.kind === "image").length, [items]);
  const filteredText = useMemo(() => items.filter((a) => a.kind === "text").length, [items]);
  const filteredVideos = useMemo(() => items.filter((a) => a.kind === "video").length, [items]);

  const saveBudget = async () => {
    try {
      const r = await client.patch("/admin/credits-budget", { monthly_budget_gbp: budgetInput || null });
      setSummary((s) => (s ? { ...s, monthly_budget_gbp: r.data.monthly_budget_gbp } : s));
      setBudgetInput(r.data.monthly_budget_gbp != null ? String(r.data.monthly_budget_gbp) : "");
      toast.success(r.data.monthly_budget_gbp != null ? "Monthly budget saved" : "Budget removed");
    } catch { toast.error("Couldn't save budget"); }
  };

  const budget = summary?.monthly_budget_gbp;
  const monthSpend = summary?.month_gbp || 0;
  const budgetPct = budget ? Math.min(100, Math.round((monthSpend / budget) * 100)) : 0;

  const memberName = (id) => {
    if (!id) return "Editorial desk";
    const m = members.find((x) => x.id === id);
    return m?.business_name || "Editorial desk";
  };

  return (
    <div data-testid="credits-page">
      <Header kicker="Spend" title="Credits">
        <a
          href="https://app.emergent.sh"
          target="_blank"
          rel="noreferrer"
          className="overline border border-ink px-4 py-2 flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors"
          data-testid="credits-top-up-link"
        >
          <ExternalLink size={13} /> Top up in Emergent Profile
        </a>
      </Header>

      <div className="p-6 sm:p-10 space-y-8">
        {/* Summary cards — running spend windows */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/15 border border-ink/15" data-testid="credits-summary-cards">
            {[
              { key: "day", label: "Today (est.)", value: summary.day_gbp },
              { key: "week", label: "Last 7 days (est.)", value: summary.week_gbp },
              { key: "month", label: "Last 30 days (est.)", value: summary.month_gbp },
              { key: "all", label: "All time (est.)", value: summary.total_gbp },
            ].map((c) => (
              <div key={c.key} className="bg-cream p-7" data-testid={`credits-card-${c.key}`}>
                <p className="font-serif text-5xl">{fmtGbp(c.value)}</p>
                <p className="overline text-ink/50 mt-2">{c.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Per-feature meters & switches */}
        <FeaturesPanel />

        {/* Monthly budget + warning */}
        {summary && (
          <div className="border border-ink/15 bg-white p-5 sm:p-6 space-y-4" data-testid="credits-budget-panel">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="overline text-ink/60">Monthly budget (optional)</p>
                <p className="font-sans text-sm text-ink/75 mt-1.5 max-w-xl leading-relaxed">
                  Set a soft cap for estimated 30-day spend and we&apos;ll flag it here when you&apos;re getting close.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-serif text-xl text-ink/60">£</span>
                <input
                  type="number" min="0" step="5" placeholder="e.g. 50"
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  className="w-28 bg-white border border-ink/25 px-3 py-2 font-sans text-sm focus:outline-none focus:border-ink"
                  data-testid="credits-budget-input"
                />
                <button onClick={saveBudget} className="overline border border-ink px-4 py-2 hover:bg-ink hover:text-cream transition-colors" data-testid="credits-budget-save">
                  Save
                </button>
              </div>
            </div>
            {budget && (
              <div data-testid="credits-budget-meter">
                <div className="flex items-baseline justify-between mb-1.5">
                  <p className="font-sans text-sm text-ink/70">
                    <b className="font-serif text-lg text-ink">{fmtGbp(monthSpend)}</b> of {fmtGbp(budget)} this month · {budgetPct}%
                  </p>
                </div>
                <div className="h-2 bg-ink/10 w-full">
                  <div
                    className={`h-2 transition-all ${budgetPct >= 100 ? "bg-red-600" : budgetPct >= 80 ? "bg-amber-500" : "bg-ink"}`}
                    style={{ width: `${budgetPct}%` }}
                  />
                </div>
                {budgetPct >= 80 && (
                  <p className={`flex items-center gap-2 font-sans text-sm mt-2.5 ${budgetPct >= 100 ? "text-red-700" : "text-amber-700"}`} data-testid="credits-budget-warning">
                    <AlertTriangle size={14} />
                    {budgetPct >= 100
                      ? "You've passed this month's budget — top up or ease off the generations."
                      : "You're over 80% of this month's budget."}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Rate card — how the estimate works */}
        {summary && (
          <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid="credits-rate-card">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="overline text-ink/60">How the estimate is calculated</p>
                <p className="font-sans text-sm text-ink/75 mt-1.5 max-w-3xl leading-relaxed">
                  Each AI call is logged the moment it&apos;s fired and priced with a realistic per-call estimate.
                  Real charges go against your Emergent Universal Key balance — check <b>Profile → Universal Key</b> in Emergent for your exact live balance.
                </p>
              </div>
              <div className="flex gap-6 shrink-0">
                <div>
                  <p className="font-serif text-3xl">{fmtGbp(summary.text_rate_gbp)}</p>
                  <p className="overline text-ink/50 mt-1">per written piece</p>
                </div>
                <div>
                  <p className="font-serif text-3xl">{fmtGbp(summary.image_rate_gbp)}</p>
                  <p className="overline text-ink/50 mt-1">per image</p>
                </div>
                <div>
                  <p className="font-serif text-3xl">{fmtGbp(summary.video_rate_gbp ?? 0.5)}</p>
                  <p className="overline text-ink/50 mt-1">per cover film</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="border border-ink/15 bg-white p-4 sm:p-5 flex flex-wrap items-center gap-3" data-testid="credits-filters">
          <p className="overline text-ink/60 mr-2">Filter</p>
          <div className="flex flex-wrap gap-1.5" data-testid="credits-range-filter">
            {RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setFilter((f) => ({ ...f, range: r.id }))}
                className={`overline px-3 py-2 border transition-colors ${filter.range === r.id ? "bg-ink text-cream border-ink" : "border-ink/25 text-ink hover:border-ink"}`}
                data-testid={`credits-range-${r.id}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="credits-kind-filter">
            {[{ id: "all", label: "All" }, { id: "text", label: "Text" }, { id: "image", label: "Image" }, { id: "video", label: "Film" }].map((k) => (
              <button
                key={k.id}
                onClick={() => setFilter((f) => ({ ...f, kind: k.id }))}
                className={`overline px-3 py-2 border transition-colors ${filter.kind === k.id ? "bg-ink text-cream border-ink" : "border-ink/25 text-ink hover:border-ink"}`}
                data-testid={`credits-kind-${k.id}`}
              >
                {k.label}
              </button>
            ))}
          </div>
          <select
            value={filter.member_id}
            onChange={(e) => setFilter((f) => ({ ...f, member_id: e.target.value }))}
            className="ml-auto bg-white border border-ink/25 px-3 py-2 font-sans text-sm focus:outline-none focus:border-ink"
            data-testid="credits-member-filter"
          >
            <option value="all">All members</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.business_name}</option>)}
          </select>
        </div>

        {/* Filtered totals bar */}
        <div className="border border-ink/15 bg-cream p-4 sm:p-5 flex flex-wrap items-baseline justify-between gap-3" data-testid="credits-filtered-totals">
          <p className="font-sans text-sm text-ink/70">
            <b className="font-serif text-2xl text-ink mr-2">{fmtGbp(filteredCost)}</b>
            estimated across <b>{items.length}</b> generation{items.length === 1 ? "" : "s"}
            <span className="text-ink/50"> · {filteredText} written · {filteredImages} images{filteredVideos > 0 ? ` · ${filteredVideos} films` : ""}</span>
          </p>
        </div>

        {/* Per-member breakdown */}
        {summary?.per_member && summary.per_member.length > 0 && filter.member_id === "all" && filter.range === "all" && (
          <div className="border border-ink/15 bg-white" data-testid="credits-per-member">
            <div className="px-5 py-4 border-b border-ink/10">
              <p className="overline text-ink/60">Spend by member (all time · est.)</p>
            </div>
            <div className="divide-y divide-ink/5">
              {summary.per_member.slice(0, 12).map((r) => (
                <div key={r.member_id || "desk"} className="px-5 py-3 flex items-center gap-3" data-testid={`credits-member-row-${r.member_id || "desk"}`}>
                  <p className="font-sans text-sm min-w-0 flex-1 truncate">
                    <b>{r.business_name || memberName(r.member_id) || "Editorial desk"}</b>
                    <span className="text-ink/50"> · {r.text} written · {r.image} images{r.video > 0 ? ` · ${r.video} films` : ""}</span>
                  </p>
                  <p className="font-serif text-lg text-ink shrink-0">{fmtGbp(r.cost_gbp)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity feed */}
        <div className="border border-ink/15 bg-white" data-testid="credits-activity-table">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink/10">
            <p className="overline text-ink flex items-center gap-2"><Receipt size={14} /> Every generation (receipts)</p>
            {loading && <span className="font-mono text-[10px] text-ink/40">Loading…</span>}
          </div>
          {items.length === 0 ? (
            <p className="px-5 py-10 font-sans text-sm text-ink/50 text-center" data-testid="credits-empty">Nothing in this window yet.</p>
          ) : (
            <div className="divide-y divide-ink/5">
              {items.map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-center gap-3" data-testid={`credits-row-${a.id}`}>
                  <span className={`p-1.5 shrink-0 ${a.kind === "image" ? "bg-gloss-gold/20 text-ink" : a.kind === "video" ? "bg-ink text-cream" : "bg-secondary text-ink/60"}`}>
                    {a.kind === "image" ? <ImageIcon size={13} /> : a.kind === "video" ? <Film size={13} /> : <PenLine size={13} />}
                  </span>
                  <p className="font-sans text-sm min-w-0 flex-1 truncate">
                    <b>{a.label}</b>
                    {a.business_name && <span className="text-ink/60"> · {a.business_name}</span>}
                    {a.detail && <span className="text-ink/50"> — {a.detail}</span>}
                  </p>
                  <p className="font-mono text-[10px] text-ink/40 shrink-0 hidden sm:block">
                    {new Date(a.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="font-serif text-sm text-ink shrink-0 w-16 text-right" data-testid={`credits-row-cost-${a.id}`}>{fmtGbp(rowCost(a))}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="font-mono text-[11px] text-ink/40" data-testid="credits-disclaimer">
          Costs shown are estimates based on typical per-call rates (£0.01 / written piece, £0.04 / image, £0.50 / cover film). For your exact live balance and spend, open Profile → Universal Key in Emergent.
        </p>
      </div>
    </div>
  );
}
